import { useMemo } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { AlertBanner, ButtonChipItem } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useLLMTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMTabs";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { navigateToReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    ExplicitLLModel,
    IAiTrackerCampaign,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { CampaignStatus, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

const ChipWrapper = styled.div`
    ${SWReactIcons} {
        width: 24px;
        height: 24px;
    }
`;

interface IPendingModelsBannerProps {
    campaign: IAiTrackerCampaign;
}

export const PendingModelsBanner = ({ campaign }: IPendingModelsBannerProps) => {
    const {
        currentTab: { id: tabId },
    } = useLLMTabs();

    const pendingModels = useMemo<ExplicitLLModel[]>(
        () =>
            Object.entries(campaign.configs)
                .filter(([modelKey, config]) => modelKey !== LLModel.All && config.status === CampaignStatus.Pending)
                .map(([modelKey]) => modelKey as ExplicitLLModel),
        [campaign],
    );

    const onModelClick = (model: ExplicitLLModel): void => {
        navigateToReport(campaign, model, tabId);
    };

    if (!pendingModels?.length) {
        return null;
    }

    return (
        <AlertBanner
            variant="info"
            shadowed
        >
            <AlertBanner.Image />
            <AlertBanner.Description>
                <Flex
                    alignItems="center"
                    gap="12px"
                >
                    {translate("overview_report.multiplatform.pending-banner")}
                    <Flex>
                        {pendingModels.map((model) => {
                            const { displayNameKey, iconName } = AI_MODELS_CONFIG[model];

                            return (
                                <ChipWrapper key={model}>
                                    <ButtonChipItem
                                        text={translate(displayNameKey)}
                                        iconName={iconName}
                                        hideActionButton
                                        onClick={() => onModelClick(model)}
                                    />
                                </ChipWrapper>
                            );
                        })}
                    </Flex>
                </Flex>
            </AlertBanner.Description>
        </AlertBanner>
    );
};
